import {ClientDashboardModel} from "../models/ClientDashboardModel";
import {ProductHistoryRecord} from "../../../stripe/models/ProductHistoryRecord";
import {DashboardFeatureState, FeatureStatus} from "../components/features/models/DashboardFeatureModel";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {Optional} from "../../../js/util/Optional";
import {StripeProductName} from "../../../stripe/models/StripeProductNames";
import {StripeProductHistory} from "../../../stripe/models/StripeProductHistory";

export abstract class ClientDashboardModelProvider {

    public abstract async model(): Promise<ClientDashboardModel>;

    protected abstract storeUrl(name: StripeProductName): string | null;

    protected abstract trialPeriod(name: StripeProductName): number;

    protected hiddenFeature(): DashboardFeatureState {
        return {
            status: FeatureStatus.INACTIVE,
            trialEnds: DateTimeUtil.isoToday(),
            trialing: false,
            visible: false,
            storeUrl: null,
            trialPeriod: 0
        };
    }

    protected activeFeature(): DashboardFeatureState {
        return {
            visible: true,
            trialing: false,
            status: FeatureStatus.ACTIVE,
            trialEnds: DateTimeUtil.isoToday(),
            storeUrl: null,
            trialPeriod: 0
        };
    }

    protected buildFeatureStatus(name: StripeProductName, history: StripeProductHistory): DashboardFeatureState {
        const productHistory = history.historyFor(name);
        if (productHistory == null) {
            return this.hiddenFeature();
        } else {
            return {
                visible: true,
                trialing: productHistory.currentlyTrialing,
                trialEnds: productHistory.trialEnds,
                status: this.currentStatus(productHistory),
                storeUrl: this.storeUrl(name),
                trialPeriod: this.trialPeriod(name)
            };
        }
    }

    protected currentStatus(history: ProductHistoryRecord): FeatureStatus {
        if (history.currentlyTrialing) {
            return FeatureStatus.TRIALING;
        } else if (!history.hasBeenTrialed) {
            return FeatureStatus.NOT_TRIALED;
        } else if (history.isCancelled || (history.endedAt != null)) {
            return FeatureStatus.INACTIVE;
        } else if (history.isCancelled) {
            return FeatureStatus.CANCELLED;
        } else {
            return FeatureStatus.ACTIVE;
        }
    }

    /* Pick the best state from the list of provided states */
    protected mergeStates(...states: Array<DashboardFeatureState>): DashboardFeatureState {

        const stateWithStatus = (status: FeatureStatus) => {
            return () => states.find((s) => s.status === status);
        };

        return Optional.empty<DashboardFeatureState>()
                       .orGet(stateWithStatus(FeatureStatus.ACTIVE))
                       .orGet(stateWithStatus(FeatureStatus.TRIALING))
                       .orGet(stateWithStatus(FeatureStatus.NOT_TRIALED))
                       .orGet(stateWithStatus(FeatureStatus.CANCELLED))
                       .orElseGet(() => states[0]);
    }
}
